// ./interceptor
/**
 * 1. Axios catch block vs interceptor
 * If using Axios, a Network Error (like when the API is down) is thrown before a response is received — so it never hits response interceptors. We need to handle it in the error interceptor or in your .catch() block.
 *
 * 2. Global error boundary (React) only catches render-time errors
 * React’s ErrorBoundary only catches errors during rendering, lifecycle methods, or constructors — not async errors like failed API calls.
 *
 * 3. Silent failure in your API wrapper
 * If you have a wrapper like api.get(...), make sure it doesn’t swallow errors silently or rethrow without triggering UI feedback.
 *
 */
import { notifications } from "@mantine/notifications";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_NETWORK_ERROR,
  MSG_DEFAULT_NETWORK_ERROR_TITLE,
  SESSION_EXPIRED,
  SESSION_EXPIRED_MSG,
} from "common/constants";
import { navigateTo } from "common/navigation";
import { safeLog } from "common/utils";
import { useCartStore } from "stores/useCartStore";
import { useSessionStore } from "stores/userSessionStore";
import { authApi, publicApi } from "./";
import { logError } from "./diagnostic.service";

const handleError = (error: any) => {
  const status = error?.response?.status;
  const method = error?.config?.method?.toLowerCase?.() ?? "get";

  if (error.message === "Network Error") {
    notifications.show({
      title: MSG_DEFAULT_NETWORK_ERROR_TITLE,
      message: MSG_DEFAULT_NETWORK_ERROR,
      color: MantineNotificationColor.Error,
      position: MantineNotificationPosition.TopCenter,
    });
    navigateTo("/application-error");
    return Promise.reject(error);
  }

  try {
    logError(error);
  } catch (err) {
    safeLog("Unhandled API Call: ", err);
  }

  // 🔐 401 = not authenticated. If the client still believes it's logged in,
  // the server-side session has died out from under it (e.g. expired/invalidated
  // between polls) — reconcile local state immediately rather than leaving the
  // UI showing a "logged in" shell that 401s on every subsequent call. This runs
  // for GET too: an authApi 401 while isLoggedIn=true is always a desync, not a
  // normal "not logged in" response.
  if (status === 401) {
    if (useSessionStore.getState().isLoggedIn) {
      useCartStore.getState().clearCart();
      useSessionStore.getState().clearUser();
      notifications.show({
        title: SESSION_EXPIRED,
        message: SESSION_EXPIRED_MSG,
        color: MantineNotificationColor.Error,
        position: MantineNotificationPosition.TopCenter,
      });
      navigateTo("/login");
    }
    return Promise.reject(error);
  }

  // 🚫 Never redirect on GET requests
  if (method === "get") {
    return Promise.reject(error);
  }

  // 🚫 403 = authenticated but forbidden → go to not-authorized
  if (status === 403) {
    navigateTo("/not-authorized");
    return Promise.reject(error);
  }

  return Promise.reject(error);
};

// Attach to both APIs
authApi.interceptors.response.use((res) => res, handleError);
publicApi.interceptors.response.use((res) => res, handleError);
